import React, { memo, forwardRef, Children } from 'react';
import { styled } from '@compiled/react';
import { CustomThemeButton } from '@atlaskit/button';
import Button from '@atlaskit/button/standard-button';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { getTheme } from '@atlassian/jira-common-theme';
import CrystalRainbowDarkNew from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/assets/crystal-rainbow-dark.svg';
import CrystalRainbowLightNew from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/assets/crystal-rainbow.svg';
import type { SectionMessageActionProps, SectionMessageProps } from './types';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ButtonStyle = styled.div({
	marginTop: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px;`,
});

const defaultBlue = token('color.background.accent.blue.bolder.hovered', '#0C66E4');
const hoverBlue = token('color.background.accent.blue.bolder', '#0055CC');
const activeBlue = token('color.background.accent.blue.bolder.hovered', '#09326C');
const defaultPurple = token('color.background.accent.purple.bolder.hovered', '#6E5DC6');
const hoverPurple = token('color.background.accent.purple.bolder', '#5E4DB2');
const activePurple = token('color.background.accent.purple.bolder.hovered', '#352C63');

const gradientButtonTheme = getTheme({
	buttonStyles: {
		default: {
			background: {
				// @ts-expect-error - TS2322 - Type '{ background: { default: string; focus: string; hover: string; active: string; }; color: { default: string; focus: string; hover: string; active: string; disabled: string; }; }' is not assignable to type 'CSSInterpolation'.
				default: `linear-gradient(to right, ${defaultPurple}, ${defaultBlue})`,
				focus: `linear-gradient(to right, ${defaultPurple}, ${defaultBlue})`,
				hover: `linear-gradient(to right, ${hoverPurple}, ${hoverBlue})`,
				active: `linear-gradient(to right, ${activePurple}, ${activeBlue})`,
			},
			color: {
				default: token('color.text.inverse', colors.N0),
				focus: token('color.text.inverse', colors.N0),
				hover: token('color.text.inverse', colors.N0),
				active: token('color.text.inverse', colors.N0),
			},
		},
	},
});

export const SectionMessageAction = memo(
	({
		children,
		onClick,
		href,
		testId,
		linkComponent,
		isPrimaryAction,
		target,
	}: SectionMessageActionProps) =>
		isPrimaryAction ? (
			<ButtonStyle>
				<CustomThemeButton
					iconBefore={<PremiumIcon label="" />}
					theme={gradientButtonTheme}
					testId={testId}
					target={target}
					shouldFitContainer
					href={href}
					component={href ? linkComponent : undefined}
					onClick={onClick}
				>
					{children}
				</CustomThemeButton>
			</ButtonStyle>
		) : (
			<ButtonStyle>
				<Button testId={testId} onClick={onClick}>
					{children}
				</Button>
			</ButtonStyle>
		),
);

SectionMessageAction.displayName = 'SectionMessageAction';

const commonSectionStyles = xcss({
	border: '1px solid #0000',
	wordBreak: 'break-word',

	borderRadius: 'border.radius.300',
	backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
	backgroundPosition: 'top right, center, center',
});

const sectionMessageLightStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: `url(${CrystalRainbowLightNew}), linear-gradient(239deg, #F3F0FF 12.51%, #E9F2FF 51.64%, #E3FAFC 87.92%) padding-box,linear-gradient(94.37deg, #9F8FEF 7.1%, #85B8FF 50.72%, #C1F0F5 92.9%) border-box;`,
	backgroundSize: '74px 62px, cover, cover',
});

const sectionMessageDarkStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: `url(${CrystalRainbowDarkNew}), linear-gradient(269.14deg, #2B2451 4.98%, #092957 83.89%) padding-box, linear-gradient(224.93deg, #8F7EE7 0.49%, #388BFF 99.95%) border-box;`,
	backgroundSize: '74px 62px, cover, cover',
});

export const SectionMessage = forwardRef<HTMLElement, SectionMessageProps>(
	({ children, actions, title, testId, isDarkMode }, ref) => {
		const actionElements = actions;
		const actionsArray = Children.toArray(actionElements);

		return (
			<Box
				as="section"
				paddingBlock="space.200"
				paddingInline="space.300"
				testId={testId}
				ref={ref}
				xcss={[
					isDarkMode ? sectionMessageDarkStyles : sectionMessageLightStyles,
					commonSectionStyles,
				]}
			>
				<Inline space="space.200" alignBlock="stretch">
					<Stack space="space.100" testId={testId && `${testId}--content`}>
						{!!title && (
							// To avoid overlap with the background image
							<Box paddingInlineEnd="space.600">
								{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
								<h4>{title}</h4>
							</Box>
						)}
						{children}
						{actionsArray.length > 0 && (
							<Inline
								shouldWrap
								testId={testId && `${testId}--actions`}
								space="space.150"
								rowSpace="space.0"
							>
								{actionsArray}
							</Inline>
						)}
					</Stack>
				</Inline>
			</Box>
		);
	},
);

SectionMessage.displayName = 'SectionMessage';

export default SectionMessage;
